import { ChantContext } from '../gregorio-chant/ChantContext';
import { LOWEST_PITCH } from '../gregorio-chant/constants';
import { GregorioGlyphType } from '../gregorio-chant/enums/GregorioGlyphType';
import { GregorioShape } from '../gregorio-chant/enums/GregorioShape';
import { GregorioType } from '../gregorio-chant/enums/GregorioType';
import { GregorioGlyph } from '../gregorio-chant/structures/GregorioGlyph';
import { GregorioNoteElement } from '../gregorio-chant/structures/GregorioNoteElement';
import { GregorioElement, GregorioSyllable } from '../gregorio-chant/structures/GregorioSyllable';
import { GregorioScore, Length, RubberLength } from '../public-api';
import { LengthUnit } from './distances';

export function calculateNabcPositionFromPitch(context: ChantContext, pitch: number) {
  return -context.nabcVerticalInterval * (pitch);
}

export function calculateHeightPositionFromPitch(context: ChantContext, pitch: number) {
  return context.staffLinesInterval * (12 - pitch);
}

export function calculateStaffPositionFromPitch(pitch: number) {
  return 12 - pitch;
}

export function calculatePositionFromLine(context: ChantContext, line: number) {
  if (line === 4) {
    return context.staffLinesInterval;
  }
  if (line === 3) {
    return 3 * context.staffLinesInterval;
  }
  if (line === 2) {
    return 5 * context.staffLinesInterval;
  }
  if (line === 1) {
    return 7 * context.staffLinesInterval;
  }

  return 0;
}

export function get_punctum_inclinatum_to_nobar_space_case(glyph: GregorioGlyph, next?: GregorioGlyph): number {
  if (glyph.notes.glyph_type <= GregorioGlyphType.PUNCTA_INCLINATA) {
    // eslint-disable-next-line max-len
    if (next && next.type == GregorioType.GLYPH && (next.notes.glyph_type == GregorioGlyphType.PUNCTUM || (next.notes.glyph_type == GregorioGlyphType.FLEXA && !next.notes.fuse_to_next_glyph))) {
      if (!glyph.lastNote || !next.firstNote) {
        return -1;
      }

      const descent = glyph.lastNote.pitch - next.firstNote.pitch;
      /* a negative descent is an ascent */
      switch (descent) {
        case -1:
          return 19;
        case 1:
          return 16;
        case -2:
          return 20;
        case 2:
          return 17;
        case -3:
        case -4:
          return 21;
        case 3:
        case 4:
          return 18;
      }
    }
  }

  return -1;
}

export function is_puncta_inclinata(glyph: number): boolean {
  return glyph <= GregorioGlyphType._5_PUNCTA_INCLINATA_ASCENDENS;
}

export function get_punctum_inclinatum_space_case(note: GregorioNoteElement, previous?: GregorioNoteElement): number {
  let temp: number;

  switch (note.shape) {
    case GregorioShape.PUNCTUM_INCLINATUM_ASCENDENS:
      if (previous) {
        /* means that it is not the first note of the puncta inclinata
         * sequence */
        temp = previous.pitch - note.pitch;
        /* negative values = ascending ambitus */
        /* not sure we ever need to consider a larger ambitus here */
        switch (temp) {
          case 1:
          case 2:
          case 3:
          case 4:
            return 25;
          case -1:
          default:
            return 12;
          case -2:
            return 14;
          case -3:
          case -4:
            return 15;
        }
      }
      break;
    case GregorioShape.PUNCTUM_INCLINATUM_STANS:
      if (previous) {
        /* means that it is not the first note of the puncta inclinata
         * sequence */
        return 26;
      }
      break;
    case GregorioShape.PUNCTUM_INCLINATUM_DESCENDENS:
      if (previous) {
        /* means that it is not the first note of the puncta inclinata
         * sequence */
        temp = previous.pitch - note.pitch;
        /* negative values = ascending ambitus */
        /* not sure we ever need to consider a larger ambitus here */
        switch (temp) {
          case 1:
          default:
            return 3;
          case 2:
            return 10;
          case 3:
          case 4:
            return 11;
          case -1:
          case -2:
          case -3:
          case -4:
            return 24;
        }
      }
      break;
    case GregorioShape.PUNCTUM_INCLINATUM_DEMINUTUS:
      if (previous) {
        /* means that it is not the first note of the puncta inclinata
         * sequence */
        temp = previous.pitch - note.pitch;
        if (temp < -2) {
          return 15;
        } else if (temp > 2) {
          return 11;
        } else {
          if (previous && previous.shape === GregorioShape.PUNCTUM_INCLINATUM_DEMINUTUS) {
            if (temp < -1) {
              /* really if the ambitus = 3rd at this point */
              return 14;
            } else if (temp > 1) {
              /* really if the ambitus = 3rd at this point */
              return 10;
            } else {
              /* temp == 0, so there is no ascending case */
              return 8;
            }
          } else {
            if (temp < 0) {
              /* puncta inclinatum followed by puncta inclinatum
               * debilis */
              return 13;
            } else if (temp > 0) {
              /* puncta inclinatum followed by puncta inclinatum
               * debilis */
              return 7;
            }
          }
        }
      } /* LCOV_EXCL_LINE */
      break;
    case GregorioShape.PUNCTUM_INCLINATUM_AUCTUS:
      if (previous) {
        /* means that it is not the first note of the puncta inclinata
         * sequence */
        temp = previous.pitch - note.pitch;
        if (temp < -1 || temp > 1) {
          /* this is the normal interglyph space, so we'll use it for
           * either direction */
          return 1;
        } else {
          /* temp == 0, so there is no ascending case */
          /* we approximate that it is the same space */
          return 3;
        }
      }
      break;
    default:
      break;
  }

  return -1;
}

export function is_before_linebreak(syllables: GregorioSyllable[], sylIndex: number, elIndex: number): boolean {
  let element = syllables[sylIndex].elements[elIndex];

  for (let sylIndex_running = sylIndex; sylIndex_running < syllables.length; sylIndex_running++) {
    if (element) {
      if (element.type == GregorioType.END_OF_LINE) {
        return true;
      }
      break;
    }
    element = syllables[sylIndex_running + 1].elements[0];
  }

  return false;
}

export function gregorio_adjust_pitch_into_staff(score: GregorioScore, pitch: number): number {
  while (pitch < LOWEST_PITCH) {
    // eslint-disable-next-line no-param-reassign
    pitch += 7;
  }
  while (pitch > score.highest_pitch) {
    // eslint-disable-next-line no-param-reassign
    pitch -= 7;
  }
  // assert(pitch >= LOWEST_PITCH && pitch <= score -> highest_pitch);
  return pitch;
}


// eslint-disable-next-line max-len
export function parse_serialized_distances(serialized: { [key: string]: string[] }): { [key: string]: Length | RubberLength } {
  const result: { [key: string]: Length | RubberLength } = {};

  const keys = Object.keys(serialized);
  for (const key of keys) {
    const value = serialized[key];
    if (value.length === 2) {
      result[key] = new Length(parseFloat(value[0]), value[1] as LengthUnit);
    } else if (value.length === 4) {
      // eslint-disable-next-line max-len
      result[key] = new RubberLength(new Length(parseFloat(value[0]), value[1] as LengthUnit), new Length(parseFloat(value[2]), value[3] as LengthUnit));
    } else if (value.length === 6) {
      // eslint-disable-next-line max-len
      result[key] = new RubberLength(new Length(parseFloat(value[0]), value[1] as LengthUnit), new Length(parseFloat(value[2]), value[3] as LengthUnit), new Length(parseFloat(value[4]), value[5] as LengthUnit));
    }
  }

  return result;
}
